import { Link } from 'react-router-dom';
import { Body1, Body1Strong, Caption1, Card, Subtitle2 } from '@fluentui/react-components';
import { ChevronRightRegular } from '@fluentui/react-icons';
import { HalfDonutChart } from '@shared/components/HalfDonutChart';
import { TERMINOLOGY } from '@shared/i18n';
import { toPercent } from '@shared/utils/percent';
import { PROJECT_VIEW_PARAM } from '../../constants';
import { TASK_STATUS_PARAM, TASK_STATUSES } from '../../tasks/constants';
import { ETaskStatus, type TTaskDashboard } from '../../tasks/types';
import { EProjectView } from '../../types';

type TProps = {
  statusCounts: TTaskDashboard['statusCounts'];
};

const getSummary = (done: number, total: number, cancelled: number) => {
  if (!total && !cancelled) return `No ${TERMINOLOGY.task.lowerPlural}`;
  if (!total) return `All ${TERMINOLOGY.task.lowerPlural} are cancelled`;
  if (done === total) return `All ${TERMINOLOGY.task.lowerPlural} completed`;
  return `${done} of ${total} ${total === 1 ? TERMINOLOGY.task.lower : TERMINOLOGY.task.lowerPlural} completed`;
};

const TaskProgressCard = ({ statusCounts }: TProps) => {
  const done = statusCounts[ETaskStatus.DONE];
  const total = statusCounts[ETaskStatus.NOT_STARTED] + statusCounts[ETaskStatus.IN_PROGRESS] + done;
  return (
    <Card size="large" className="min-w-0 flex-[3_1_560px]">
      <Subtitle2>{TERMINOLOGY.task.upper} progress</Subtitle2>

      <div className="flex flex-1 flex-wrap items-center justify-center gap-x-8 gap-y-6">
        <div className="flex flex-col items-center gap-2">
          <HalfDonutChart percent={toPercent(done, total)} />
          <Body1 className="text-neutralFg-2">{getSummary(done, total, statusCounts[ETaskStatus.CANCELLED])}</Body1>
        </div>

        <div className="flex min-w-0 grow basis-60 flex-col">
          {TASK_STATUSES.map(({ value, label, color, icon: Icon }) => (
            <Link
              key={value}
              to={{ search: `?${PROJECT_VIEW_PARAM}=${EProjectView.TASKS}&${TASK_STATUS_PARAM}=${value}` }}
              className="flex items-center gap-3 border-b border-neutralStroke-2 px-2 py-2.5 last:border-b-0 hover:bg-neutralBg-3"
            >
              <Icon fontSize={20} className="shrink-0" style={{ color }} />
              <Body1Strong className="w-6">{statusCounts[value]}</Body1Strong>
              <Body1 className="grow">{label}</Body1>
              <Caption1 className="shrink-0 whitespace-nowrap text-neutralFg-3">
                {value === ETaskStatus.CANCELLED ? 'Not counted' : `${toPercent(statusCounts[value], total)}%`}
              </Caption1>
              <ChevronRightRegular className="shrink-0 text-neutralFg-3" />
            </Link>
          ))}
        </div>
      </div>
    </Card>
  );
};
export default TaskProgressCard;
